import { memo, useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import LoadingSpinner from '../components/LoadingSpinner.jsx'
import DevicePairingWizard from '../components/DevicePairingWizard.jsx'

// Fleet statistics configuration for reusability
const FLEET_STATS_CONFIG = [
  { key: 'total', label: 'Total Devices', color: 'primary', getValue: (devices) => devices.length },
  { key: 'online', label: 'Online', color: 'success', getValue: (devices) => devices.filter(d => d.connectionStatus === 'online').length },
  { key: 'warning', label: 'Warnings', color: 'warning', getValue: (devices) => devices.filter(d => d.healthStatus === 'warning').length },
  { key: 'critical', label: 'Critical', color: 'danger', getValue: (devices) => devices.filter(d => d.healthStatus === 'critical').length },
]

// Fleet action buttons configuration
const FLEET_ACTIONS = [
  { key: 'add', label: 'Add Device', icon: 'plus-circle', color: 'primary', action: 'showPairing' },
  { key: 'export', label: 'Export Fleet Data', icon: 'download', color: 'outline-primary', action: 'export' },
  { key: 'analytics', label: 'Fleet Analytics', icon: 'bar-chart', color: 'outline-info', action: 'analytics' },
  { key: 'config', label: 'Bulk Configuration', icon: 'gear', color: 'outline-secondary', action: 'config' },
]

// Device metrics configuration for preview
const DEVICE_METRICS = [
  { label: 'Voltage', value: '12.8V' },
  { label: 'Current', value: '15.2A' },
  { label: 'Temp', value: '68°F' },
]

// Memoized Fleet Statistics Card Component
function FleetStatCardComponent({ value, label, color }) {
  return (
    <div className="col-md-3">
      <div className="card text-center">
        <div className="card-body">
          <h3 className={`text-${color}`}>{value}</h3>
          <p className="card-text text-muted">{label}</p>
        </div>
      </div>
    </div>
  )
}
const FleetStatCard = memo(FleetStatCardComponent)

// Memoized Device Card Component
function DeviceCardComponent({ device, onDeviceClick }) {
  const statusBadgeClass = useMemo(() => {
    switch (device.connectionStatus) {
      case 'online': return 'bg-success'
      case 'offline': return 'bg-danger'
      default: return 'bg-warning'
    }
  }, [device.connectionStatus])

  const handleClick = useCallback(() => {
    onDeviceClick(device.deviceId)
  }, [device.deviceId, onDeviceClick])

  return (
    <div className="col-md-6 col-lg-4 mb-3">
      <div 
        className="card h-100 cursor-pointer border-hover"
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      >
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="flex-grow-1">
              <h6 className="card-title mb-1">
                {device.nickname || device.deviceId}
              </h6>
              {device.location && (
                <small className="text-muted">{device.location}</small>
              )}
            </div>
            
            <span className={`badge ${statusBadgeClass}`}>
              {device.connectionStatus || 'unknown'}
            </span>
          </div>
          
          {/* Device Metrics Preview */}
          <div className="row text-center small">
            {DEVICE_METRICS.map((metric) => (
              <div key={metric.label} className="col">
                <div className="text-muted">{metric.label}</div>
                <div className="fw-bold">{metric.value}</div>
              </div>
            ))}
          </div>
          
          {/* Last Seen */}
          <div className="mt-3 text-center">
            <small className="text-muted">
              Last seen: {device.lastSeen ? new Date(device.lastSeen).toLocaleString() : 'Never'}
            </small>
          </div>
        </div>
      </div>
    </div>
  )
}
const DeviceCard = memo(DeviceCardComponent)

// Memoized Action Button Component
function ActionButtonComponent({ action, onAction }) {
  const handleClick = useCallback(() => {
    onAction(action.action)
  }, [action.action, onAction])

  return (
    <div className="col-md-3 mb-3">
      <button 
        className={`btn btn-${action.color} w-100`}
        onClick={handleClick}
      >
        <i className={`bi bi-${action.icon} me-2`}></i>
        {action.label}
      </button>
    </div>
  )
}
const ActionButton = memo(ActionButtonComponent)

/**
 * Fleet Overview page component - React 19+ optimized
 * Provides fleet-wide perspective across all devices with optimized re-renders
 */
function FleetOverview() {
  const navigate = useNavigate()
  const [showPairingWizard, setShowPairingWizard] = useState(false)
  const { 
    devices, 
    devicesLoading, 
    selectDevice, 
    setFleetView,
    hasDevices, 
  } = useCloudContext()
  
  // Memoized fleet statistics to prevent recalculation on re-renders
  const fleetStats = useMemo(() => 
    FLEET_STATS_CONFIG.map(stat => ({
      ...stat,
      value: stat.getValue(devices || []),
    }))
  , [devices])
  
  // Optimized event handlers with useCallback
  const handleDeviceClick = useCallback((deviceId) => {
    selectDevice(deviceId)
    setFleetView(false)
    navigate('/dashboard')
  }, [selectDevice, setFleetView, navigate])
  
  const handleBackToDevice = useCallback(() => {
    setFleetView(false)
    navigate('/dashboard')
  }, [setFleetView, navigate])

  const handleFleetAction = useCallback((actionType) => {
    switch (actionType) {
      case 'showPairing':
        setShowPairingWizard(true)
        break
      case 'export':
        // TODO: Implement fleet data export
        break
      case 'analytics':
        // TODO: Navigate to fleet analytics
        break
      case 'config':
        // TODO: Implement bulk configuration
        break
      default:
        break
    }
  }, [])

  const handleClosePairingWizard = useCallback(() => {
    setShowPairingWizard(false)
  }, [])

  // Show loading while checking for devices
  if (devicesLoading) {
    return <LoadingSpinner message="Loading fleet overview..." />
  }

  // No devices state
  if (!hasDevices) {
    return (
      <div>
        <div className="text-center py-5">
          <i className="bi bi-collection" style={{ fontSize: '4rem', color: 'var(--text-light-muted)' }}></i>
          <h4 className="mt-3 mb-3">No Fleet to Display</h4>
          <p className="text-muted mb-4">
            Add multiple devices to access fleet management features.
          </p>
          
          <button 
            className="btn btn-primary"
            onClick={() => handleFleetAction('showPairing')}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Add Your First Device
          </button>
        </div>
        
        <DevicePairingWizard 
          isOpen={showPairingWizard}
          onClose={handleClosePairingWizard}
        />
      </div>
    )
  }

  return (
    <div>
      {/* Fleet Overview Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-1">Fleet Overview</h1>
          <p className="text-muted mb-0">
            Managing {devices.length} device{devices.length !== 1 ? 's' : ''}
          </p>
        </div>
        
        {/* Fleet Actions */}
        <div className="btn-group" role="group">
          <button 
            type="button" 
            className="btn btn-outline-secondary btn-sm"
            onClick={handleBackToDevice}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Back to Device
          </button>
          
          <button 
            type="button" 
            className="btn btn-outline-primary btn-sm"
            onClick={() => {
              // TODO: Implement fleet refresh
            }}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh All
          </button>
          
          <button 
            type="button" 
            className="btn btn-outline-success btn-sm"
            onClick={() => handleFleetAction('showPairing')}
          >
            <i className="bi bi-plus me-1"></i>
            Add Device
          </button>
        </div>
      </div>
      
      {/* Fleet Statistics */}
      <div className="row mb-4">
        {fleetStats.map((stat) => (
          <FleetStatCard 
            key={stat.key}
            value={stat.value}
            label={stat.label}
            color={stat.color}
          />
        ))}
      </div>
      
      {/* Device Grid */}
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">All Devices</h5>
        </div>
        <div className="card-body">
          <div className="row">
            {devices.map((device) => (
              <DeviceCard
                key={device.deviceId}
                device={device}
                onDeviceClick={handleDeviceClick}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* Fleet Management Actions */}
      <div className="card mt-4">
        <div className="card-header">
          <h5 className="card-title mb-0">Fleet Actions</h5>
        </div>
        <div className="card-body">
          <div className="row">
            {FLEET_ACTIONS.map((action) => (
              <ActionButton
                key={action.key}
                action={action}
                onAction={handleFleetAction}
              />
            ))}
          </div>
        </div>
      </div>
      
      <DevicePairingWizard 
        isOpen={showPairingWizard}
        onClose={handleClosePairingWizard}
      />
    </div>
  )
}

export default memo(FleetOverview)
